/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

/**
 * Application permission modules, mirroring `Authorization::ModuleRegistry` on the server.
 *
 * The registry is the authority: the tick-box grid in Admin App Users is rendered from
 * `GET /v1/admin/permission_modules` rather than from anything here. What this file owns is the
 * part the API cannot answer — which *frontend route* and which *menu entry* belong to which
 * module key. Keys are duplicated only so a typo in a route rule fails a test instead of silently
 * gating nothing.
 */

import { EMPTY_GRANT_MEANS_NO_ACCESS } from './moduleAccessRollout'

// Every key the server can hand back in `accessible_modules`, in registry order.
export const MODULE_KEYS = [
  // ANALYTICS
  'dashboard',
  'reports',
  'audit_logs',
  'perq_card_logs',
  'import_logs',
  // MANAGEMENT
  'restaurant_groups',
  'restaurants',
  'restaurant_management',
  'configured_perqs',
  'employee_groups',
  'perq_cards',
  'dynamic_perqs',
  'jobs',
  'policy_engine',
  'reservations',
  // USERS
  'mobile_app_users',
  'admin_app_users',
  // SYSTEM
  'configuration',
]

const MODULE_KEY_SET = new Set(MODULE_KEYS)

/**
 * Route prefix → module key.
 *
 * Prefixes rather than exact paths, so the dozens of `/admin/reports/*` screens need one rule and
 * a new report page is gated the day it is added. Matching is on segment boundaries (see
 * `moduleForPath`), which is what keeps `/admin/employees` from swallowing `/admin/employee_groups`.
 *
 * `restaurant_management` covers the revenue-centre, sales-category and meal-period screens —
 * mirroring `Authorization::ModuleRegistry`'s own `restaurant_management` controller mapping on
 * the server, which claims the same three controllers.
 */
export const ROUTE_MODULE_RULES = {
  '/admin/dashboard': 'dashboard',
  '/admin/reports': 'reports',
  '/admin/audit_logs': 'audit_logs',
  '/admin/perq_card_logs': 'perq_card_logs',
  '/admin/import_logs': 'import_logs',
  '/admin/groups': 'restaurant_groups',
  '/admin/restaurant': 'restaurants',
  '/admin/revenue_centers': 'restaurant_management',
  '/admin/sales_categories': 'restaurant_management',
  '/admin/meal_periods': 'restaurant_management',
  '/admin/configured_perqs': 'configured_perqs',
  '/admin/employee_groups': 'employee_groups',
  '/admin/perq_cards': 'perq_cards',
  '/admin/dynamic_perqs': 'dynamic_perqs',
  '/admin/jobs': 'jobs',
  '/admin/policy_engine': 'policy_engine',
  '/admin/home': 'reservations',
  '/admin/employees': 'mobile_app_users',
  '/admin/network_guests': 'mobile_app_users',
  '/admin/guest_details': 'mobile_app_users',
  '/admin/user_management': 'admin_app_users',
  '/admin/configurations': 'configuration',
}

// Longest first, so `/admin/perq_card_logs` is tested before `/admin/perq_cards` could ever be
// reached by a shorter prefix. Sorted once at module load rather than on every navigation.
const SORTED_ROUTE_RULES = Object.entries(ROUTE_MODULE_RULES).sort(
  ([left], [right]) => right.length - left.length
)

/**
 * Authenticated, `PrivateRoute`-wrapped paths deliberately left unresolved to any module —
 * `ModuleRoute` lets them through unconditionally, because the page enforces access some other way.
 * Kept as data, mirroring the server's `RouteCoverageAllowlist`, so the coverage test can tell
 * "decided this needs a different gate" apart from "forgot to map it".
 */
export const ROLE_ONLY_PATHS = {
  // Super Admin/Viewer only, exactly like the API's own role guard on this controller. No module
  // fits: every module is grantable to instance-level users now (none are `top_level_only` any
  // more), so mapping this to one would let a granted instance-level admin through anyway. The
  // page's own role check is the real gate, independent of module access.
  '/admin/permission_profiles': 'role-gated (Super Admin/Viewer), not module-gated',
}

/**
 * Authenticated routes deliberately left ungated, with the reason. Kept as data so the drift test
 * can tell "decided to leave open" apart from "forgotten".
 *
 * Most own no module at all. `/admin/policy_engine/inactivity_review` is the exception: it sits
 * under the `/admin/policy_engine` prefix and so does resolve to a module, but it renders outside
 * the route guard entirely, which is what exempts it.
 */
export const UNGATED_PATHS = {
  // The destination of every denial. Gating it would loop.
  '/admin/no_permissions': 'the redirect target for a denied module',
  // Devise flows: a user mid-password-reset has no menu and no module to check.
  '/admin/reset_password': 'password reset, reached before the app proper',
  '/admin/forgot_password': 'password reset, reached before the app proper',
  '/reset_forgot_password': 'password reset, reached before the app proper',
  // Opened from a digest email, rendered outside Layout, and it reads `/v1/admin/me` into local
  // state rather than the store — so there is no loaded `accessible_modules` here to gate on.
  // The screen's own API calls go through the policy-engine controllers, which server-side
  // enforcement covers.
  '/admin/policy_engine/inactivity_review': 'email deep link that manages its own /me',
  // The emailed token is the only credential; there may be no admin session at all.
  '/inactivity_confirmation': 'token-authenticated guest screen',
}

/**
 * Where the app sends a user who has not asked for anywhere in particular.
 */
export const DEFAULT_LANDING_PATH = '/admin/dashboard'

/**
 * Landing candidates in sidebar order, so a user who cannot open the dashboard lands on the first
 * thing their menu actually shows rather than bouncing off the no-access screen.
 */
export const MODULE_LANDING_PATHS = [
  { module: 'dashboard', path: DEFAULT_LANDING_PATH },
  { module: 'reports', path: '/admin/reports/homepage' },
  { module: 'audit_logs', path: '/admin/audit_logs' },
  { module: 'perq_card_logs', path: '/admin/perq_card_logs' },
  { module: 'import_logs', path: '/admin/import_logs' },
  { module: 'restaurant_groups', path: '/admin/groups' },
  { module: 'restaurants', path: '/admin/restaurant' },
  { module: 'restaurant_management', path: '/admin/revenue_centers' },
  { module: 'configured_perqs', path: '/admin/configured_perqs' },
  { module: 'employee_groups', path: '/admin/employee_groups' },
  { module: 'perq_cards', path: '/admin/perq_cards' },
  { module: 'dynamic_perqs', path: '/admin/dynamic_perqs' },
  { module: 'jobs', path: '/admin/jobs' },
  { module: 'policy_engine', path: '/admin/policy_engine' },
  { module: 'reservations', path: '/admin/home' },
  { module: 'mobile_app_users', path: '/admin/employees' },
  { module: 'admin_app_users', path: '/admin/user_management' },
  { module: 'configuration', path: '/admin/configurations/integrations' },
]

export const NO_ACCESS_PATH = '/admin/no_permissions'

/**
 * @param {string} key
 * @returns {boolean} true when the server could hand this key back
 */
export const isKnownModuleKey = (key) => MODULE_KEY_SET.has(key)

/**
 * The module that owns a route, or `null` when none does.
 *
 * A `null` answer means "not module-owned" and the caller should let the request through — the
 * same fail-open rule the server applies to unmapped endpoints. Anything genuinely restricted has
 * a rule; anything without one is shared plumbing or an auth flow.
 *
 * @param {string} pathname
 * @returns {string|null}
 */
export const moduleForPath = (pathname) => {
  if (typeof pathname !== 'string' || pathname === '') return null

  // React Router matches on the decoded path, while `useLocation().pathname` hands us the raw,
  // still-encoded string — so `/admin/user%5Fmanagement` must decode here too, or it resolves to
  // "not module-owned" while the router renders it as `/admin/user_management`. Mirrors
  // @remix-run/router's own `decodePath`: decode per segment, then re-escape any `/` a decode just
  // produced back to `%2F`, so an embedded `%2F` can't invent a segment boundary here that the router
  // itself refuses to create. A malformed escape (`%zz`) throws, and resolves to no module rather
  // than crashing the guard it protects.
  let decodedPathname
  try {
    decodedPathname = pathname
      .split('/')
      .map((segment) => decodeURIComponent(segment).replace(/\//g, '%2F'))
      .join('/')
  } catch {
    return null
  }

  // React Router matches paths case-insensitively (`caseSensitive` defaults to false and none of
  // App.js's routes set it), so `/ADMIN/USER_MANAGEMENT` renders the same screen as
  // `/admin/user_management` while resolving to a different, unmapped string here. Lowering the
  // path before matching is what keeps a casing variant from resolving to "not module-owned" and
  // sailing straight through the guard in ModuleRoute. The rule keys are already lowercase.
  const lowered = decodedPathname.toLowerCase()

  // Trailing slashes reach here from hand-typed URLs; `/admin/reports/` must resolve like
  // `/admin/reports`.
  const path = lowered.length > 1 ? lowered.replace(/\/+$/, '') : lowered

  const matched = SORTED_ROUTE_RULES.find(
    ([prefix]) => path === prefix || path.startsWith(`${prefix}/`)
  )

  return matched ? matched[1] : null
}

/**
 * First path the given module set can open, in sidebar order.
 *
 * `modules` being null or undefined means the payload never carried the field — an older server or
 * a stale cache — and is treated as unrestricted, so a missing field can never lock anyone out. An
 * empty array is a real answer: this user was granted nothing.
 *
 * @param {string[]|null|undefined} modules
 * @returns {string}
 */
export const resolveLandingPath = (modules) => {
  if (!Array.isArray(modules)) return DEFAULT_LANDING_PATH

  // Mirrors `useModuleAccess`'s own `emptyBeforeBackfill` case: an empty grant is not yet
  // distinguishable from "not backfilled" until EMPTY_GRANT_MEANS_NO_ACCESS is verified, so it must
  // not send a working admin to the no-access screen while the menu and router both still treat them
  // as unrestricted.
  if (modules.length === 0 && !EMPTY_GRANT_MEANS_NO_ACCESS) return DEFAULT_LANDING_PATH

  const granted = new Set(modules)
  const landing = MODULE_LANDING_PATHS.find((entry) => granted.has(entry.module))

  return landing ? landing.path : NO_ACCESS_PATH
}
